export default class SearchBar {
  constructor(element) {
    this.header = document.getElementById("header");
    this.element = element;
    this.url = this.element.dataset.url;
    this.searchInputElement = element.querySelector(".js-search-input");
    this.suggestionsUl = element.querySelector(".js-search-suggestions");
    this.productsUl = element.querySelector(".js-search-products");
    this.articlesUl = element.querySelector(".js-search-articles");
    this.websiteUl = element.querySelector(".js-search-website");
    this.searchResultsWrapper = element.querySelector(".js-search-results-wrapper");

    this.timeout;
    this.toggleDropdownEvent = new Event("search-wrapper-toggled");
    this.searchWrapperIsShown = false;

    this.boundFunction = this.checkIfWithinBounds.bind(this);
    this.boundNavigationFunction = this.navigateWithArrowKeys.bind(this);

    this.query = "";

    this.searchInputElement.addEventListener("input", (e) => {
      this.query = e.target.value.replace(/[^a-zA-Z0-9 ]/g, "").trim();

      if (!this.query.length) {
        if (this.searchWrapperIsShown) {
          // Hide wrapper only if shown.
          this.element.dispatchEvent(this.toggleDropdownEvent);
        }
        return;
      }

      this.throttledFetch(400);
    });

    this.searchInputElement.addEventListener("click", () => {
      if (!this.searchWrapperIsShown && this.query.length) {
        this.element.dispatchEvent(this.toggleDropdownEvent);
      }
    });

    this.element.addEventListener("search-wrapper-toggled", () => {
      this.searchWrapperIsShown = !this.searchWrapperIsShown;
      this.searchResultsWrapper.classList.toggle("hidden", !this.searchWrapperIsShown);

      if (this.searchWrapperIsShown) {
        document.addEventListener("click", this.boundFunction);
        document.addEventListener("keydown", this.boundNavigationFunction);
      } else {
        document.removeEventListener("click", this.boundFunction);
        document.removeEventListener("keydown", this.boundNavigationFunction);
      }
    });

    this.header.addEventListener("header-toggled", (e) => {
      const { state } = e.detail;
      if (state === "down" && this.searchWrapperIsShown) {
        this.element.dispatchEvent(this.toggleDropdownEvent);
      }
    });
  }

  throttledFetch(delay) {
    clearTimeout(this.timeout);
    this.timeout = setTimeout(() => {
      // Reset these variables on new fetch
      this.suggestionElements = [];
      this.arrowIndex = 0;
      this.firstActive = false;

      this.loadOptions();
    }, delay);
  }

  loadOptions() {
    const urlWithQuery = `${this.url}?q=${encodeURIComponent(this.query)}`;

    this.element.classList.add("searching");
    fetch(urlWithQuery, {
      retries: 3,
      retryDelay: 1000,
    })
      .then((data) => {
        return data.json();
      })
      .then((data) => {
        const { suggestions, products, articles } = data;
        this.suggestionsUl.innerHTML = "";
        this.productsUl.innerHTML = "";
        this.articlesUl.innerHTML = "";

        if (this.websiteUl) {
          this.websiteUl.innerHTML = "";
        }

        this.buildSuggestions(suggestions);

        this.buildDetailedSuggestions(products, this.productsUl);
        this.buildDetailedSuggestions(articles, this.articlesUl);

        if (data.website) {
          this.buildDetailedSuggestions(data.website, this.websiteUl);
        }

        this.suggestionElements = this.searchResultsWrapper.querySelectorAll("li");

        if (!this.searchWrapperIsShown) {
          this.element.dispatchEvent(this.toggleDropdownEvent);
        }
      })
      .finally(() => {
        this.element.classList.remove("searching");
      });
  }

  checkIfWithinBounds(event) {
    const withinBoundaries = event.composedPath().includes(this.searchResultsWrapper);

    if (!withinBoundaries && event.target != this.searchInputElement) {
      this.element.dispatchEvent(this.toggleDropdownEvent);
    }
  }

  navigateWithArrowKeys(event) {
    switch (event.key) {
      case "ArrowDown":
        event.view.event.preventDefault(); // Disables scrolling of body element.

        if (this.arrowIndex === 0 && !this.firstActive) {
          this.firstActive = true;
          this.suggestionElements[this.arrowIndex].classList.add("active");
          this.suggestionElements[this.arrowIndex].querySelector("a").focus();
          return;
        }
        if (this.arrowIndex < this.suggestionElements.length - 1) {
          this.updateSelectedSuggestion(true);
        }
        break;
      case "ArrowUp":
        event.view.event.preventDefault();

        if (this.arrowIndex > 0) {
          this.updateSelectedSuggestion();
        }
        if (this.arrowIndex === 0) {
          this.firstActive = true;
        }

        break;
      case "Escape":
        this.element.dispatchEvent(this.toggleDropdownEvent);
        break;
      default:
        break;
    }
  }

  updateSelectedSuggestion(shouldIncrement) {
    this.firstActive = false;

    this.suggestionElements[this.arrowIndex].classList.remove("active");

    shouldIncrement ? this.arrowIndex++ : this.arrowIndex--;
    this.suggestionElements[this.arrowIndex].classList.add("active");
    this.suggestionElements[this.arrowIndex].querySelector("a").focus();
  }

  buildSuggestions(suggestions) {
    const url = new URL(this.element.action, window.location);
    url.searchParams.set(this.searchInputElement.name, this.query);
    // Always adds user input as a "suggestion"

    this.suggestionsUl.insertAdjacentHTML(
      "beforeend",
      `
    <li>
      <a href="${url}">
        <i class="icon icon-search"></i>
        <div><strong>${this.query}</strong></div>
      </a>
    </li>
    `
    );

    url.searchParams.delete(this.searchInputElement.name);

    suggestions.forEach((suggestion) => {
      url.searchParams.set(this.searchInputElement.name, suggestion.name);
      this.suggestionsUl.insertAdjacentHTML(
        "beforeend",
        `
        <li>
          <a href="${url}">
            <i class="icon icon-search"></i>
            <div>${this.highlight(suggestion.name)}</div>
          </a>
        </li>
        `
      );
    });
  }

  buildDetailedSuggestions(items, wrapper) {
    wrapper.parentElement.classList.toggle("d-none", !items.length > 0);
    items.forEach((item) => {
      wrapper.insertAdjacentHTML(
        "beforeend",
        `
        <li>
          <a href="${item.url}">
            <div class="image-wrapper">
              <img srcset="${item.image1xUrl},"${item.image2xUrl} 2x, "${item.image3xUrl} 3x" src="${item.image1xUrl}" alt="${item.name}" />
            </div>
            <div>${this.highlight(item.name)}</div>
          </a>
        </li>
        `
      );
    });
  }

  highlight(string) {
    return string.replace(RegExp("(" + this.query + ")", "i"), "<strong>$1</strong>");
  }
}
